import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import client, { setSession } from "@/lib/api";

export default function Login() {
  const [email, setEmail] = useState("");
  const [magicToken, setMagicToken] = useState("");
  const [step, setStep] = useState("email"); // email | sent | fallback
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  // If arriving from an email link with ?token=..., verify automatically
  useEffect(() => {
    const urlToken = params.get("token");
    if (!urlToken) return;
    setLoading(true);
    client
      .post("/auth/verify", { token: urlToken })
      .then(({ data }) => {
        setSession(data.session_token);
        toast.success("Signed in. Welcome to the reader.");
        navigate("/read");
      })
      .catch(() => {
        toast.error("This link is invalid or expired. Request a new one below.");
        setLoading(false);
      });
  }, [params, navigate]);

  const requestLink = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await client.post("/auth/magic-link", { email });
      if (data.email_sent) {
        setStep("sent");
        toast.success("Magic link sent — check your inbox.");
      } else {
        setMagicToken(data.magic_token);
        setStep("fallback");
        toast.success("Magic link generated. Click below to sign in.");
      }
    } catch {
      toast.error("Could not send magic link.");
    }
    setLoading(false);
  };

  const useFallbackLink = async () => {
    setLoading(true);
    try {
      const { data } = await client.post("/auth/verify", { token: magicToken });
      setSession(data.session_token);
      toast.success("Signed in. Welcome to the reader.");
      navigate("/read");
    } catch {
      toast.error("Invalid or expired token.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[80vh] bg-mizizi-cream flex items-center justify-center px-6 py-24">
      <div className="max-w-md w-full text-center">
        <div className="overline text-mizizi-terracotta">Magic Link</div>
        <h1 className="font-serif font-light text-5xl md:text-6xl mt-6 leading-none text-mizizi-charcoal">Sign in.</h1>
        <p className="mt-6 text-mizizi-charcoal/70">No passwords. We send a link to your inbox.</p>

        {step === "email" && (
          <form onSubmit={requestLink} className="mt-10 space-y-4">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              placeholder="you@example.com"
              disabled={loading}
              className="w-full px-5 py-4 bg-transparent border border-mizizi-gold/50 focus:outline-none focus:border-mizizi-terracotta text-center disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="pill pill-primary w-full justify-center disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send Magic Link"}
            </button>
          </form>
        )}

        {step === "sent" && (
          <div className="mt-10 space-y-6" data-testid="magic-link-sent">
            <div className="mx-auto w-16 h-16 border border-mizizi-gold/50 rounded-full flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#B8873B" strokeWidth="1.4">
                <path d="M4 6h16v12H4z"/>
                <path d="M4 6l8 7 8-7"/>
              </svg>
            </div>
            <p className="font-serif text-2xl text-mizizi-charcoal leading-snug">Check your inbox.</p>
            <p className="text-mizizi-charcoal/70 text-sm">
              We sent a sign-in link to <span className="text-mizizi-terracotta">{email}</span>. It's good for 24 hours.
            </p>
            <button
              onClick={() => { setStep("email"); setEmail(""); }}
              data-testid="try-different-email"
              className="overline text-mizizi-charcoal/60 hover:text-mizizi-terracotta"
            >
              Use different email
            </button>
          </div>
        )}

        {step === "fallback" && (
          <div className="mt-10 space-y-6">
            <p className="text-mizizi-charcoal/70">
              Email is not configured for this environment. Use the button below:
            </p>
            <button
              onClick={useFallbackLink}
              disabled={loading}
              data-testid="use-magic-link"
              className="pill pill-primary w-full justify-center disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign In with Magic Link"}
            </button>
            <button
              onClick={() => setStep("email")}
              data-testid="try-different-email"
              className="overline text-mizizi-charcoal/60 hover:text-mizizi-terracotta"
            >
              Use different email
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
